/**
 * RISPACS-1037 — AI Dashboard client.
 */

import { API_ENDPOINTS, API_CONFIG } from '../config/api.config';
import { apiGet } from '../utils/apiClient';
import authService from '../auth/authService';

export interface AiDashboardKpis {
  total_processed_studies: number;
  normal_count: number;
  abnormal_count: number;
  completed_attempts: number;
  failed_attempts: number;
  timeout_attempts: number;
  success_rate: number | null;
  avg_processing_seconds: number | null;
  reviewed_count: number;
  agreement_rate: number | null;
  disagreement_rate: number | null;
}

export interface AiDashboardTrendPoint {
  bucket: string;
  completed: number;
  failed: number;
  timeout: number;
  normal: number;
  abnormal: number;
  failed_processing: number;
}

export interface AiDashboardFacilityRow {
  facility_id: number;
  facility_name: string;
  total_processed_studies: number;
  normal_count: number;
  abnormal_count: number;
  success_rate: number | null;
  avg_processing_seconds: number | null;
  disagreement_rate: number | null;
}

export interface AiDashboardData {
  filters: {
    from: string;
    to: string;
    facility_ids: number[];
    classification: string | null;
    status: string | null;
    trend: string;
    facilityName?: string;
  };
  generated_at: string;
  empty?: boolean;
  kpis: AiDashboardKpis;
  trend: AiDashboardTrendPoint[];
  by_facility: AiDashboardFacilityRow[];
  by_model: Array<{ model_version: string; count: number }>;
  filterOptions?: {
    facilities: Array<{ id: number; name: string }>;
  };
}

export interface AiDashboardQuery {
  from?: string;
  to?: string;
  facility_ids?: string;
  classification?: string;
  status?: string;
  trend?: string;
}

function buildQuery(params: AiDashboardQuery): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '' && String(value) !== 'all') {
      q.append(key, String(value));
    }
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export async function getAiDashboard(
  params: AiDashboardQuery
): Promise<{ success: boolean; data?: AiDashboardData; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.ANALYTICS.AI_DASHBOARD}${buildQuery(params)}`;
    const response = await apiGet<{ success: boolean; data: AiDashboardData; message?: string }>(
      url
    );
    if (!response.data?.success || !response.data.data) {
      return { success: false, error: response.data?.message || 'Failed to load AI dashboard' };
    }
    return { success: true, data: response.data.data };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to load AI dashboard',
    };
  }
}

export async function exportAiDashboard(
  params: AiDashboardQuery,
  format: 'csv' | 'excel' | 'xlsx'
): Promise<void> {
  const authHeader = authService.getAuthHeader();
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '' && String(value) !== 'all') {
      q.append(key, String(value));
    }
  });
  q.set('format', format === 'xlsx' ? 'excel' : format);
  const url = `${API_ENDPOINTS.ANALYTICS.AI_DASHBOARD_EXPORT}?${q.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: '*/*',
      'tenant-id': API_CONFIG.TENANT_ID,
      ...(authHeader.Authorization ? { Authorization: authHeader.Authorization } : {}),
    },
  });

  if (!response.ok) {
    let message = 'Export failed';
    try {
      const body = await response.json();
      message = body?.message || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const ext = format === 'csv' ? 'csv' : 'xls';
  const filename = match?.[1] || `AI_Dashboard_${Date.now()}.${ext}`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
